import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleAlertIcon,
  RotateCwIcon,
  Trash2Icon,
} from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { QuoteChanges } from 'types/suggestions/QuoteSuggestions';
import { cn } from 'utils/shadcn';

import {
  LTLQuoteResultsVariant,
  LTLQuoteResultsView,
} from '../LTL/LTLQuoteResultsView';
import { UnifiedTaiLTLForm } from '../LTL/Tai/UnifiedTaiLTLForm';
import { LTLAccessorial, LTLQuote, QuickQuoteInputs } from '../types';
import { validateLTLForm } from '../utils/validation';
import { BatchLTLStatusChip } from './BatchLTLStatusChip';
import {
  BatchLTLShipmentInput,
  BatchLTLShipmentRun,
  BatchLTLShipmentStatus,
  BatchLTLShipmentValidation,
} from './types';
import {
  getFreightSummary,
  getStopFullLabel,
  getStopZipLabel,
} from './utils/shipmentDisplay';
import { BATCH_LTL_VALIDATION_OPTIONS } from './utils/validateBatchLTLShipment';

type BatchLTLShipmentCardProps = {
  shipmentIndex: number;
  shipment: BatchLTLShipmentInput;
  status: BatchLTLShipmentStatus;
  validation: BatchLTLShipmentValidation;
  run: Undef<BatchLTLShipmentRun>;
  selectedQuoteIds: string[];
  accessorials: LTLAccessorial[];
  // The shipment's extraction, for the stops' AI-filled hints; null for an added shipment.
  suggestedFields: Maybe<QuoteChanges>;
  // Set when every shipment ships from the same place; the panel shows it once.
  sharedOriginLabel: Maybe<string>;
  isSelected: boolean;
  isExpanded: boolean;
  isIncludedInReply: boolean;
  isRateDisabled: boolean;
  onSelectedChange: (isSelected: boolean) => void;
  onExpandedChange: (isExpanded: boolean) => void;
  onIncludedInReplyChange: (isIncluded: boolean) => void;
  onToggleQuote: (quote: LTLQuote) => void;
  onRate: () => void;
  onRemove: () => void;
  onOpenInQuickQuote?: () => void;
};

// Batch-only header (label, status, lane, freight, notes, reply and remove controls) over
// the single LTL form and results. Must render inside the shipment's BatchLTLShipmentForm.
export function BatchLTLShipmentCard({
  shipmentIndex,
  shipment,
  status,
  validation,
  run,
  selectedQuoteIds,
  accessorials,
  suggestedFields,
  sharedOriginLabel,
  isSelected,
  isExpanded,
  isIncludedInReply,
  isRateDisabled,
  onSelectedChange,
  onExpandedChange,
  onIncludedInReplyChange,
  onToggleQuote,
  onRate,
  onRemove,
  onOpenInQuickQuote,
}: BatchLTLShipmentCardProps) {
  const formMethods = useFormContext<QuickQuoteInputs>();
  const { getValues, setError, clearErrors } = formMethods;
  const isInvalid = status === BatchLTLShipmentStatus.Invalid;
  const isFailed = status === BatchLTLShipmentStatus.Failed;
  const isInReply = status === BatchLTLShipmentStatus.InReply;
  const isRated = status === BatchLTLShipmentStatus.Rated || isInReply;
  const hasQuotes = (run?.quotes.length ?? 0) > 0;
  const origin = getStopZipLabel(shipment.stops[0]) || 'Pickup not set';
  const destination =
    getStopFullLabel(shipment.stops[shipment.stops.length - 1]) ||
    'Delivery not set';
  const freightSummary = getFreightSummary(
    shipment.ltl?.items ?? [],
    shipment.ltl?.commodity?.freightClass
  );
  const errorPathsKey = [...validation.errorPaths].join('|');

  // Missing fields show on the single form's own inputs while the card is open.
  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    clearErrors();
    validateLTLForm(getValues(), setError, BATCH_LTL_VALIDATION_OPTIONS);
  }, [isExpanded, errorPathsKey, getValues, setError, clearErrors]);

  return (
    <Flex
      direction='col'
      gap='xs'
      data-testid={`batch-ltl-shipment-${shipment.key}`}
      className={cn(
        'w-full rounded-md border bg-neutral-50 px-2.5 py-2',
        isInvalid || isFailed ? 'border-error-300' : 'border-neutral-300',
        isFailed && 'bg-error-50/60',
        !isIncludedInReply && 'border-dashed'
      )}
    >
      <Flex align='start' gap='sm' className='w-full'>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelectedChange(checked === true)}
          aria-label={`Select shipment ${shipmentIndex + 1}`}
          className='mt-0.5'
        />
        <button
          type='button'
          className='min-w-0 flex-1 text-left'
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${shipment.label}`}
          onClick={() => onExpandedChange(!isExpanded)}
        >
          <Flex align='center' gap='xs' className='w-full'>
            <Typography
              variant='body-xxs'
              textColor={isInvalid || isFailed ? 'error' : 'moreMuted'}
              className='tabular-nums'
              as='span'
            >
              #{shipmentIndex + 1}
            </Typography>
            <Typography
              variant='body-xs'
              weight='semibold'
              className='min-w-0 flex-1 truncate'
              title={shipment.label}
              as='span'
            >
              {shipment.label}
            </Typography>
            <BatchLTLStatusChip
              status={status}
              rateCount={
                status === BatchLTLShipmentStatus.Rated
                  ? run?.quotes.length
                  : undefined
              }
            />
            {isExpanded ? (
              <ChevronUpIcon className='h-3.5 w-3.5 shrink-0 text-neutral-500' />
            ) : (
              <ChevronDownIcon className='h-3.5 w-3.5 shrink-0 text-neutral-500' />
            )}
          </Flex>
          <Flex
            align='center'
            gap='xs'
            wrap='wrap'
            className='w-full'
            data-testid='batch-ltl-route'
          >
            {!sharedOriginLabel && (
              <Typography
                variant='body-xs'
                textColor='lessMuted'
                className='whitespace-nowrap tabular-nums'
                as='span'
              >
                {origin}
              </Typography>
            )}
            <ArrowRightIcon className='h-3 w-3 shrink-0 text-neutral-400' />
            <Typography variant='body-xs' textColor='lessMuted' as='span'>
              {destination}
            </Typography>
          </Flex>
          {!isExpanded && freightSummary && (
            <Typography
              variant='body-xxs'
              textColor='muted'
              className='block'
              as='span'
            >
              {freightSummary}
            </Typography>
          )}
          {shipment.notes && (
            <Typography
              variant='body-xxs'
              textColor='muted'
              className='block italic'
              as='span'
            >
              {shipment.notes}
            </Typography>
          )}
          {isInvalid && (
            <Typography
              variant='body-xs'
              weight='medium'
              textColor='error'
              className='block'
              as='span'
            >
              Missing: {validation.missingFields.join(', ')}
            </Typography>
          )}
        </button>
      </Flex>

      <Flex justify='between' align='center' className='w-full pl-6'>
        <Checkbox
          label='In reply'
          labelClassName='text-xs text-neutral-700'
          checked={isIncludedInReply}
          onCheckedChange={(checked) =>
            onIncludedInReplyChange(checked === true)
          }
          aria-label={`Include ${shipment.label} in the reply`}
        />
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-6 px-1 text-xs text-neutral-500 hover:bg-error-50 hover:text-error-600'
          onClick={onRemove}
          aria-label={`Remove ${shipment.label}`}
          buttonNamePosthog={ButtonNamePosthog.BatchLTLQuoteRemoveShipment}
        >
          <Trash2Icon className='mr-1 h-3 w-3' />
          Remove
        </Button>
      </Flex>

      {isFailed && (
        <Flex direction='col' gap='xs' className='w-full pl-6'>
          <Flex align='start' gap='xs' className='w-full'>
            <CircleAlertIcon className='mt-0.5 h-3.5 w-3.5 shrink-0 text-error-500' />
            <Typography variant='body-xs' className='flex-1 text-error-700'>
              {run?.failureMessage}
            </Typography>
          </Flex>
          <Flex align='center' gap='sm' className='w-full'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 px-0 text-xs text-info-600 hover:bg-transparent hover:underline'
              disabled={isRateDisabled}
              onClick={onRate}
              buttonNamePosthog={ButtonNamePosthog.BatchLTLQuoteRetryShipment}
            >
              <RotateCwIcon className='mr-1 h-3 w-3' />
              Retry
            </Button>
            {onOpenInQuickQuote && (
              <button
                type='button'
                className='text-xs text-info-600 underline-offset-2 hover:underline'
                onClick={onOpenInQuickQuote}
              >
                Open in Quick Quote
              </button>
            )}
          </Flex>
        </Flex>
      )}

      {run && (isRated || (isFailed && hasQuotes)) && (
        <div className='w-full pl-6'>
          <LTLQuoteResultsView
            variant={LTLQuoteResultsVariant.Compact}
            quotes={run.quotes}
            selectedQuoteIds={selectedQuoteIds}
            externalQuote={run.externalQuote}
            shiplifyLookups={shipment.ltl?.shiplifyLookups}
            onSelectQuote={onToggleQuote}
            isActiveTaiLtlIntegration
          />
        </div>
      )}

      {isExpanded && (
        <Flex
          direction='col'
          gap='sm'
          className='mt-1 w-full border-t border-neutral-200 pt-2'
          data-testid='batch-ltl-tai-form'
        >
          <UnifiedTaiLTLForm
            formMethods={formMethods}
            hasAddedQuoteDates={false}
            accessorials={accessorials}
            integrationName={TMS.Tai}
            showQuoteInformation={false}
            isCompact
            suggestedFields={suggestedFields}
          />
          {isRated && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 self-end px-2 text-xs text-info-600 hover:bg-info-50'
              disabled={isRateDisabled || !validation.isValid}
              onClick={onRate}
              buttonNamePosthog={null}
            >
              <RotateCwIcon className='mr-1 h-3 w-3' />
              Re-rate with changes
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
}
